import type { Payout } from '@/types/payout';
import type { GlobalSettings } from '@/types/settings';
import { encryptData, decryptData } from './encryption';
import { Sentry } from './sentry';
import { getAuthState } from '@/contexts/AuthContext';
import { payoutApiService } from '@/services/payoutApiService';
import { api } from '@/services/apiService';

const PAYOUTS_STORAGE_KEY = 'cryptotrack_payouts';
const SETTINGS_STORAGE_KEY = 'cryptotrack_settings';
const ENCRYPTED_PAYOUTS_KEY = 'cryptotrack_encrypted_payouts';
const ENCRYPTED_SETTINGS_KEY = 'cryptotrack_encrypted_settings';
const HIDDEN_TOKENS_STORAGE_KEY = 'cryptotrack_hidden_tokens';

// Generate a browser-specific key for encryption
const generateBrowserKey = async (): Promise<string> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('CryptoTrack fingerprint', 2, 2);
  }

  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvas.toDataURL(),
    'cryptotrack-salt'
  ].join('|');

  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// ──────────────────────────────────────────────────────────────────────────────
// LocalStorage helpers (Guest mode)
// ──────────────────────────────────────────────────────────────────────────────

export const savePayoutsToLocalStorage = async (payouts: Payout[]): Promise<void> => {
  try {
    const browserKey = await generateBrowserKey();
    const encryptedData = await encryptData(JSON.stringify(payouts), browserKey);
    localStorage.setItem(ENCRYPTED_PAYOUTS_KEY, encryptedData);
    localStorage.removeItem(PAYOUTS_STORAGE_KEY);
  } catch (error) {
    console.error('Erreur lors de la sauvegarde chiffrée:', error);
    Sentry.captureException(error, { tags: { operation: 'savePayoutsToLocalStorage' } });
    try {
      localStorage.setItem(PAYOUTS_STORAGE_KEY, JSON.stringify(payouts));
    } catch (fallbackError) {
      Sentry.captureException(fallbackError, { tags: { operation: 'savePayoutsToLocalStorage_fallback' } });
    }
  }
};

export const loadPayoutsFromLocalStorage = async (): Promise<Payout[]> => {
  try {
    const encryptedData = localStorage.getItem(ENCRYPTED_PAYOUTS_KEY);
    if (encryptedData) {
      const browserKey = await generateBrowserKey();
      const decryptedData = await decryptData(encryptedData, browserKey);
      const payouts = JSON.parse(decryptedData) as Payout[];
      return payouts.map((p) => ({ ...p, type: p.type || 'payout' }));
    }

    const unencryptedData = localStorage.getItem(PAYOUTS_STORAGE_KEY);
    if (unencryptedData) {
      const payouts = JSON.parse(unencryptedData) as Payout[];
      const migrated = payouts.map((p) => ({ ...p, type: p.type || 'payout' }));
      await savePayoutsToLocalStorage(migrated);
      return migrated;
    }

    return [];
  } catch (error) {
    console.error('Erreur lors du chargement:', error);
    console.warn(
      '⚠️ Déchiffrement des payouts échoué. Le fingerprint du navigateur a peut-être changé.' +
      ' Utilisez la fonctionnalité d\'import/export avec mot de passe pour récupérer vos données.'
    );
    Sentry.captureException(error, { tags: { operation: 'loadPayoutsFromLocalStorage' } });

    try {
      const stored = localStorage.getItem(PAYOUTS_STORAGE_KEY);
      if (stored) {
        return (JSON.parse(stored) as Payout[]).map((p) => ({ ...p, type: p.type || 'payout' }));
      }
      return [];
    } catch (fallbackError) {
      Sentry.captureException(fallbackError, { tags: { operation: 'loadPayoutsFromLocalStorage_fallback' } });
      return [];
    }
  }
};

// ──────────────────────────────────────────────────────────────────────────────
// Public API — hybrid (API when authenticated, LocalStorage in Guest mode)
// ──────────────────────────────────────────────────────────────────────────────

export const loadPayoutsFromStorage = async (): Promise<Payout[]> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      return await payoutApiService.list();
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'loadPayoutsFromStorage_api' }, level: 'warning' });
      // Graceful fallback to local read-only when API is unreachable
      return loadPayoutsFromLocalStorage();
    }
  }
  return loadPayoutsFromLocalStorage();
};

export const savePayoutsToStorage = async (payouts: Payout[]): Promise<void> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      // Bulk replace via import endpoint (used after data management operations)
      await payoutApiService.bulkImport(payouts);
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'savePayoutsToStorage_api' } });
    }
    return;
  }
  return savePayoutsToLocalStorage(payouts);
};

export const addPayoutToStorage = async (payout: Payout): Promise<Payout[]> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      await payoutApiService.create(payout);
      return payoutApiService.list();
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'addPayoutToStorage_api' } });
      throw error;
    }
  }
  const payouts = await loadPayoutsFromLocalStorage();
  const updatedPayouts = [payout, ...payouts];
  await savePayoutsToLocalStorage(updatedPayouts);
  return updatedPayouts;
};

// ──────────────────────────────────────────────────────────────────────────────
// Settings — hybrid
// ──────────────────────────────────────────────────────────────────────────────

const defaultSettings: GlobalSettings = {
  declarationFrequency: 'trimestriel',
  electricityCosts: {
    tariffType: 'simple',
    simpleTariff: 0.1740
  },
  solarPanels: {
    enabled: false
  },
  bncRate: 0.34
};

function apiRowToSettings(row: Record<string, unknown>): GlobalSettings {
  return {
    declarationFrequency: (row.declaration_frequency as GlobalSettings['declarationFrequency']) ?? defaultSettings.declarationFrequency,
    electricityCosts: {
      tariffType: (row.electricity_tariff_type as GlobalSettings['electricityCosts']['tariffType']) ?? 'simple',
      simpleTariff: row.electricity_simple_tariff != null ? Number(row.electricity_simple_tariff) : undefined,
      hcTariff: row.electricity_hc_tariff != null ? Number(row.electricity_hc_tariff) : undefined,
      hpTariff: row.electricity_hp_tariff != null ? Number(row.electricity_hp_tariff) : undefined,
    },
    solarPanels: {
      enabled: Boolean(row.solar_panels_enabled),
      averageDailyProduction: row.solar_daily_production != null ? Number(row.solar_daily_production) : undefined,
    },
    bncRate: row.bnc_rate != null ? Number(row.bnc_rate) : defaultSettings.bncRate,
  };
}

export const saveSettingsToLocalStorage = async (settings: GlobalSettings): Promise<void> => {
  try {
    const browserKey = await generateBrowserKey();
    const encryptedData = await encryptData(JSON.stringify(settings), browserKey);
    localStorage.setItem(ENCRYPTED_SETTINGS_KEY, encryptedData);
    localStorage.removeItem(SETTINGS_STORAGE_KEY);
  } catch (error) {
    console.error('Erreur lors de la sauvegarde chiffrée des paramètres:', error);
    Sentry.captureException(error, { tags: { operation: 'saveSettingsToLocalStorage' } });
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (fallbackError) {
      Sentry.captureException(fallbackError, { tags: { operation: 'saveSettingsToLocalStorage_fallback' } });
    }
  }
};

export const loadSettingsFromLocalStorage = async (): Promise<GlobalSettings> => {
  try {
    const encryptedData = localStorage.getItem(ENCRYPTED_SETTINGS_KEY);
    if (encryptedData) {
      const browserKey = await generateBrowserKey();
      const decryptedData = await decryptData(encryptedData, browserKey);
      const parsedSettings = JSON.parse(decryptedData);
      return { ...defaultSettings, ...parsedSettings, bncRate: parsedSettings.bncRate ?? defaultSettings.bncRate };
    }

    const unencryptedData = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (unencryptedData) {
      const parsedSettings = JSON.parse(unencryptedData);
      const settings = { ...defaultSettings, ...parsedSettings, bncRate: parsedSettings.bncRate ?? defaultSettings.bncRate };
      await saveSettingsToLocalStorage(settings);
      return settings;
    }

    return defaultSettings;
  } catch (error) {
    console.error('Erreur lors du chargement des paramètres:', error);
    console.warn('⚠️ Déchiffrement des paramètres échoué. Les paramètres par défaut seront utilisés.');
    Sentry.captureException(error, { tags: { operation: 'loadSettingsFromLocalStorage' } });

    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        const parsedSettings = JSON.parse(stored);
        return { ...defaultSettings, ...parsedSettings, bncRate: parsedSettings.bncRate ?? defaultSettings.bncRate };
      }
    } catch (fallbackError) {
      Sentry.captureException(fallbackError, { tags: { operation: 'loadSettingsFromLocalStorage_fallback' } });
    }

    return defaultSettings;
  }
};

export const loadSettingsFromStorage = async (): Promise<GlobalSettings> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      const { data } = await api.get<Record<string, unknown>>('/settings');
      return apiRowToSettings(data);
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'loadSettingsFromStorage_api' }, level: 'warning' });
      return loadSettingsFromLocalStorage();
    }
  }
  return loadSettingsFromLocalStorage();
};

export const saveSettingsToStorage = async (settings: GlobalSettings): Promise<void> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      await api.put('/settings', settings);
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'saveSettingsToStorage_api' } });
      throw error; // Propagate so the caller can show an error toast
    }
    return;
  }
  return saveSettingsToLocalStorage(settings);
};

export const clearLocalStorage = (): void => {
  localStorage.removeItem(ENCRYPTED_PAYOUTS_KEY);
  localStorage.removeItem(ENCRYPTED_SETTINGS_KEY);
  localStorage.removeItem(PAYOUTS_STORAGE_KEY);
  localStorage.removeItem(SETTINGS_STORAGE_KEY);
  localStorage.removeItem('cryptotrack_mining_configs');
  localStorage.removeItem(HIDDEN_TOKENS_STORAGE_KEY);
};

// ──────────────────────────────────────────────────────────────────────────────
// Hidden tokens (per-device UI preference — not synced with the API/account)
// ──────────────────────────────────────────────────────────────────────────────

export const getHiddenTokenIds = (): string[] => {
  try {
    const stored = localStorage.getItem(HIDDEN_TOKENS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'getHiddenTokenIds' } });
    return [];
  }
};

export const setHiddenTokenIds = (tokenIds: string[]): void => {
  localStorage.setItem(HIDDEN_TOKENS_STORAGE_KEY, JSON.stringify(tokenIds));
};
